import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import type { JoinPreview, JoinRegisterInput, JoinRequestInput } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { useSession } from '../lib/session';
import { AuthLayout } from '../components/AuthLayout';
import { NewPasswordField } from '../components/PasswordField';
import { Alert, Button, Field, LoadingBlock } from '../components/ui';

type Role = 'parent' | 'player';

/** The page behind a team's join link: register (or sign in) and ask to join. A coach approves. */
export function JoinPage() {
  const { code = '' } = useParams();
  const here = `/join/${code}`;
  const { data: session } = useSession();
  const preview = useQuery({ queryKey: ['join', code], queryFn: () => api.get<JoinPreview>(`/join/${code}`), retry: false });
  const [done, setDone] = useState<{ kind: 'check_email' } | { kind: 'requested'; emailConfirmed: boolean } | { kind: 'member'; teamId: string } | null>(null);

  if (preview.isLoading) {
    return (
      <AuthLayout title="Opening the team’s link…">
        <LoadingBlock />
      </AuthLayout>
    );
  }
  if (preview.isError || !preview.data) {
    return (
      <AuthLayout title="This link isn’t working">
        <Alert>{errorMessage(preview.error)}</Alert>
        <Link to="/" className="btn-secondary mt-5 w-full">
          Go to Hoopify
        </Link>
      </AuthLayout>
    );
  }
  const team = preview.data;
  const title = `Join ${team.teamName}${team.ageGroup ? ` (${team.ageGroup})` : ''}`;
  const user = session?.status === 'signed_in' ? session.user : null;

  if (done) {
    return (
      <AuthLayout title={done.kind === 'member' ? `You’re on ${team.teamName}` : 'Request sent'}>
        {done.kind === 'member' ? (
          <Link to={`/teams/${done.teamId}`} className="btn-primary btn-lg w-full">
            Open the team
          </Link>
        ) : (
          <div className="space-y-4">
            <p className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-ok" aria-hidden />
              {done.kind === 'check_email' || !done.emailConfirmed ? (
                <span>
                  <strong className="font-semibold">Check your email</strong> and select the link to confirm your address. Then the coach can see your request.
                </span>
              ) : (
                <span>The coach will look at your request and you’ll get an email once you’re in.</span>
              )}
            </p>
            <p className="text-[14px] text-muted">Until a coach approves you, you won’t see the team’s sessions.</p>
          </div>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={title} description="Ask to join as a player or a parent. A coach approves each person, then you’ll see the team’s sessions and what to bring.">
      {user ? (
        <SignedInForm code={code} name={user.name} email={user.email} onDone={setDone} />
      ) : !team.signupsOpen ? (
        <div className="space-y-4">
          <Alert tone="info">Registrations open soon. If you already have a Hoopify account, sign in to ask to join.</Alert>
          <Link to={`/login?next=${encodeURIComponent(here)}`} className="btn-secondary w-full">
            Sign in
          </Link>
        </div>
      ) : (
        <>
          <RegisterForm code={code} onDone={() => setDone({ kind: 'check_email' })} />
          <p className="mt-5 text-center text-[14px] text-muted">
            Already have an account?{' '}
            <Link to={`/login?next=${encodeURIComponent(here)}`} className="font-medium text-action hover:underline">
              Sign in
            </Link>
          </p>
        </>
      )}
    </AuthLayout>
  );
}

function RolePicker({ value, onChange }: { value: Role | null; onChange: (r: Role) => void }) {
  const option = (r: Role, label: string, hint: string) => (
    <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 ${value === r ? 'border-action bg-action-soft' : 'border-line hover:bg-sunken'}`}>
      <input type="radio" name="join-role" className="mt-1 size-4 accent-[var(--action)]" checked={value === r} onChange={() => onChange(r)} />
      <span>
        <span className="block font-medium">{label}</span>
        <span className="block text-[13px] text-muted">{hint}</span>
      </span>
    </label>
  );
  return (
    <fieldset className="space-y-2">
      <legend className="mb-1.5 text-[14px] font-medium">I’m joining as</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {option('parent', 'A parent', 'Of a player on the team')}
        {option('player', 'A player', 'On the team myself')}
      </div>
    </fieldset>
  );
}

function PlayerNameField({ role, value, onChange, error }: { role: Role | null; value: string; onChange: (v: string) => void; error?: string }) {
  if (!role) return null;
  return (
    <Field
      label={role === 'parent' ? 'Your child’s name' : 'Your name, as the coach knows it'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      maxLength={80}
      hint={role === 'parent' ? 'So the coach can match you to the right player. Got two on the team? Ask again with the other name after.' : undefined}
    />
  );
}

function RegisterForm({ code, onDone }: { code: string; onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role | null>(null);
  const [playerName, setPlayerName] = useState('');
  const [touchedPlayer, setTouchedPlayer] = useState(false);
  const register = useMutation({
    mutationFn: () => api.post(`/join/${code}/register`, { name, email, password, role: role!, playerName: role === 'player' && !touchedPlayer ? name : playerName } satisfies JoinRegisterInput),
    onSuccess: onDone,
  });
  const f = fieldErrors(register.error);
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); register.mutate(); }} className="space-y-4" noValidate>
      {register.isError && !Object.keys(f).length && <Alert>{errorMessage(register.error)}</Alert>}
      <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} error={f.name} autoComplete="name" maxLength={80} />
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={f.email} autoComplete="email" />
      <NewPasswordField value={password} onChange={setPassword} error={f.password} userInputs={[name, email]} />
      <RolePicker value={role} onChange={setRole} />
      {f.role && <p className="text-[13px] text-danger">{f.role}</p>}
      <PlayerNameField role={role} value={role === 'player' && !touchedPlayer ? name : playerName} onChange={(v) => { setTouchedPlayer(true); setPlayerName(v); }} error={f.playerName} />
      <Button type="submit" loading={register.isPending} disabled={!role} className="btn-primary btn-lg w-full">
        Ask to join
      </Button>
      <p className="text-center text-[13px] text-muted">
        This makes a free Hoopify account. Read the{' '}
        <a href="https://hoopify.com.au/privacy" className="underline">
          privacy policy
        </a>
        .
      </p>
    </form>
  );
}

function SignedInForm({ code, name, email, onDone }: { code: string; name: string; email: string; onDone: (d: { kind: 'requested'; emailConfirmed: boolean } | { kind: 'member'; teamId: string }) => void }) {
  const [role, setRole] = useState<Role | null>(null);
  const [playerName, setPlayerName] = useState('');
  const [touched, setTouched] = useState(false);
  const ask = useMutation({
    mutationFn: () =>
      api.post<{ status: 'requested'; emailConfirmed: boolean } | { status: 'member'; teamId: string }>(`/join/${code}/request`, { role: role!, playerName: role === 'player' && !touched ? name : playerName } satisfies JoinRequestInput),
    onSuccess: (r) => onDone(r.status === 'member' ? { kind: 'member', teamId: r.teamId } : { kind: 'requested', emailConfirmed: r.emailConfirmed }),
  });
  const f = fieldErrors(ask.error);
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); ask.mutate(); }} className="space-y-4" noValidate>
      <p className="text-[14px] text-muted">
        Signed in as <strong className="font-semibold text-ink">{email}</strong>.
      </p>
      {ask.isError && !Object.keys(f).length && <Alert>{errorMessage(ask.error)}</Alert>}
      <RolePicker value={role} onChange={setRole} />
      <PlayerNameField role={role} value={role === 'player' && !touched ? name : playerName} onChange={(v) => { setTouched(true); setPlayerName(v); }} error={f.playerName} />
      <Button type="submit" loading={ask.isPending} disabled={!role} className="btn-primary btn-lg w-full">
        Ask to join
      </Button>
    </form>
  );
}
